import type { RecoveryMarker, RecoveryMarkerRead } from './authStore';

/** Durable half of the password-recovery latch (V2-018). Stores only the
 *  recovering user id, used solely for an equality check against a session
 *  Supabase already verified. Guarded so storage denial cannot break auth. */
const STORAGE_KEY = 'hpos.auth.recovery.v1';

/** Not UUID-strict: rejecting a real id would drop a real latch. */
const VALID_USER_ID = /^[A-Za-z0-9._:-]{1,200}$/;

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}


export function browserRecoveryMarker(): RecoveryMarker {
  return {
    read(): RecoveryMarkerRead {
      const store = storage();
      if (!store) return { kind: 'unavailable' };
      let raw: string | null;
      try {
        raw = store.getItem(STORAGE_KEY);
      } catch {
        return { kind: 'unavailable' };
      }
      if (raw === null) return { kind: 'absent' };
      if (!VALID_USER_ID.test(raw)) {
        try {
          store.removeItem(STORAGE_KEY);
        } catch {
          /* nothing further can be done */
        }
        return { kind: 'absent' };
      }
      return { kind: 'present', userId: raw };
    },

    set(userId: string): void {
      if (!VALID_USER_ID.test(userId)) return;
      const store = storage();
      if (!store) return;
      try {
        store.setItem(STORAGE_KEY, userId);
      } catch {
        /* denied or over quota: only reload durability is lost */
      }
    },

    clear(): void {
      const store = storage();
      if (!store) return;
      try {
        store.removeItem(STORAGE_KEY);
      } catch {
        /* nothing further can be done */
      }
    },
  };
}
